// Bespoke voxel prefabs for building kinds that previously fell back to
// placeholder mappings (e.g. library → tower, granary → house). Each is
// authored to be visually distinct in the instanced voxel renderer.
//
// Coordinate convention: origin at the building's base on the tile. Y up,
// X right, Z forward (toward camera). Buildings typically span a 4-6
// voxel footprint and stand 3-8 voxels tall.
//
// Voxel scale: 1/16 world unit per voxel. A standard 5×5 tile building is
// 80×80 world units — these prefabs render around 1 hex tile wide.

import type { BlockVoxel, StructurePrefab } from './types.js';

/** Optional per-voxel material overrides passed to `box()`. */
interface VoxelMaterialOpts {
  readonly roughness?: number;
  readonly metalness?: number;
  readonly emissive?: string;
}

function box(
  sx: number,
  sy: number,
  sz: number,
  w: number,
  h: number,
  d: number,
  color: string,
  type?: string,
  opts?: VoxelMaterialOpts,
): BlockVoxel[] {
  const out: BlockVoxel[] = [];
  for (let x = sx; x < sx + w; x++) {
    for (let y = sy; y < sy + h; y++) {
      for (let z = sz; z < sz + d; z++) {
        out.push({ x, y, z, color, ...(type ? { type } : {}), ...opts });
      }
    }
  }
  return out;
}

const STONE = '#7a7a7a';
const DARK_STONE = '#4a4a4a';
const WOOD = '#5a3a1a';
const DARK_WOOD = '#3a2010';

// ── Production / Civilian ─────────────────────────────────────────────

const forgeHut: StructurePrefab = {
  name: 'Forge Hut',
  blocks: [
    ...box(-2, 0, -2, 5, 1, 5, STONE),
    ...box(-2, 1, -2, 5, 3, 5, WOOD),
    ...box(-2, 4, -2, 5, 1, 5, '#8b1a1a'),
    // Chimney
    ...box(-2, 1, -2, 1, 5, 1, DARK_STONE),
    // Hearth glow
    ...box(-1, 1, -2, 3, 1, 1, '#ff6622'),
    // Anvil
    { x: 0, y: 1, z: 1, color: '#222222' },
    { x: 0, y: 2, z: 1, color: '#444444' },
    // Door
    ...box(0, 1, 2, 1, 2, 1, DARK_WOOD),
  ],
};

const granary: StructurePrefab = {
  name: 'Granary',
  blocks: [
    ...box(-2, 0, -2, 5, 1, 5, STONE),
    // Stone foundation
    ...box(-2, 1, -2, 5, 1, 5, '#888888'),
    // Cylindrical silo (approx with square + chamfer)
    ...box(-1, 2, -1, 3, 4, 3, '#c9a063'),
    ...box(-2, 2, -2, 1, 4, 1, '#c9a063'),
    ...box(2, 2, -2, 1, 4, 1, '#c9a063'),
    ...box(-2, 2, 2, 1, 4, 1, '#c9a063'),
    ...box(2, 2, 2, 1, 4, 1, '#c9a063'),
    // Conical roof
    ...box(-1, 6, -1, 3, 1, 3, '#5a3a1a'),
    ...box(0, 7, 0, 1, 1, 1, '#5a3a1a'),
    // Grain sacks at base
    { x: 2, y: 1, z: 0, color: '#d4a04a' },
    { x: -3, y: 1, z: 0, color: '#d4a04a' },
  ],
};

const well: StructurePrefab = {
  name: 'Well',
  blocks: [
    ...box(-1, 0, -1, 3, 1, 3, STONE),
    ...box(-1, 1, -1, 3, 2, 3, DARK_STONE, 'stone'),
    // Hollow center for water
    { x: 0, y: 2, z: 0, color: '#3a5a8a' },
    // Wooden posts
    { x: -2, y: 3, z: -2, color: WOOD },
    { x: 2, y: 3, z: -2, color: WOOD },
    { x: -2, y: 4, z: -2, color: WOOD },
    { x: 2, y: 4, z: -2, color: WOOD },
    // Crossbeam
    ...box(-2, 5, -2, 5, 1, 1, WOOD),
    // Roof
    ...box(-2, 6, -2, 5, 1, 3, '#5a3a1a'),
    ...box(-1, 7, -1, 3, 1, 1, '#5a3a1a'),
    // Bucket on rope
    { x: 0, y: 3, z: 0, color: '#704030' },
  ],
};

const herbalistHut: StructurePrefab = {
  name: "Herbalist's Hut",
  blocks: [
    ...box(-2, 0, -2, 5, 1, 5, STONE),
    ...box(-2, 1, -2, 5, 3, 5, WOOD),
    // Mossy green roof
    ...box(-2, 4, -2, 5, 1, 5, '#4a7c4a'),
    ...box(-1, 5, -1, 3, 1, 3, '#4a7c4a'),
    ...box(0, 6, 0, 1, 1, 1, '#4a7c4a'),
    // Drying herbs hanging
    { x: 1, y: 4, z: 2, color: '#88aa44' },
    { x: -1, y: 4, z: 2, color: '#aa6644' },
    // Door
    ...box(0, 1, 2, 1, 2, 1, DARK_WOOD),
    // Planter boxes
    ...box(-3, 1, 1, 1, 1, 1, '#4a7c4a'),
    ...box(3, 1, 1, 1, 1, 1, '#4a7c4a'),
  ],
};

const library: StructurePrefab = {
  name: 'Library',
  blocks: [
    ...box(-2, 0, -2, 5, 1, 5, STONE),
    // Main reading room
    ...box(-2, 1, -2, 5, 4, 5, '#aa8866'),
    // Tower with scrolls
    ...box(-2, 1, -2, 1, 7, 1, STONE),
    ...box(2, 1, -2, 1, 7, 1, STONE),
    ...box(-2, 1, 2, 1, 7, 1, STONE),
    ...box(2, 1, 2, 1, 7, 1, STONE),
    // Spire
    ...box(-1, 8, -1, 3, 1, 3, DARK_STONE),
    ...box(0, 9, 0, 1, 2, 1, DARK_STONE),
    // Windows with light
    ...box(-1, 2, 2, 3, 1, 1, '#88ccff'),
    ...box(-1, 3, 2, 3, 1, 1, '#88ccff'),
    // Book spines on shelves visible
    { x: -1, y: 2, z: -1, color: '#aa3333' },
    { x: 0, y: 2, z: -1, color: '#3333aa' },
    { x: 1, y: 2, z: -1, color: '#33aa33' },
    { x: -1, y: 3, z: -1, color: '#aaaa33' },
    { x: 0, y: 3, z: -1, color: '#aa33aa' },
    { x: 1, y: 3, z: -1, color: '#33aaaa' },
    // Door
    ...box(0, 1, 2, 1, 2, 1, DARK_WOOD),
  ],
};

const bank: StructurePrefab = {
  name: 'Bank',
  blocks: [
    ...box(-2, 0, -2, 5, 1, 5, DARK_STONE),
    ...box(-2, 1, -2, 5, 4, 5, STONE, 'stone'),
    // Marble columns at corners
    { x: -2, y: 1, z: -2, color: '#e0e0e0' },
    { x: 2, y: 1, z: -2, color: '#e0e0e0' },
    { x: -2, y: 1, z: 2, color: '#e0e0e0' },
    { x: 2, y: 1, z: 2, color: '#e0e0e0' },
    { x: -2, y: 2, z: -2, color: '#e0e0e0' },
    { x: 2, y: 2, z: -2, color: '#e0e0e0' },
    { x: -2, y: 2, z: 2, color: '#e0e0e0' },
    { x: 2, y: 2, z: 2, color: '#e0e0e0' },
    { x: -2, y: 3, z: -2, color: '#e0e0e0' },
    { x: 2, y: 3, z: -2, color: '#e0e0e0' },
    { x: -2, y: 3, z: 2, color: '#e0e0e0' },
    { x: 2, y: 3, z: 2, color: '#e0e0e0' },
    // Gold-trimmed cornice
    ...box(-2, 4, -2, 5, 1, 5, '#d4af37'),
    // Pediment
    ...box(-2, 5, -2, 5, 1, 3, '#e0e0e0'),
    ...box(-1, 6, -1, 3, 1, 1, '#e0e0e0'),
    // Coin emblem
    { x: 0, y: 5, z: 0, color: '#ffcc44' },
    // Vault door
    ...box(0, 1, 2, 1, 2, 1, '#444444'),
    { x: 0, y: 2, z: 2, color: '#d4af37' },
  ],
};

// ── Food ────────────────────────────────────────────────────────────────

const pasture: StructurePrefab = {
  name: 'Pasture',
  blocks: [
    // Grassy base
    ...box(-3, 0, -3, 7, 1, 7, '#4a7c3a'),
    // Fence rails (perimeter)
    ...box(-3, 1, -3, 7, 1, 1, WOOD),
    ...box(-3, 1, 3, 7, 1, 1, WOOD),
    ...box(-3, 1, -3, 1, 1, 7, WOOD),
    ...box(3, 1, -3, 1, 1, 7, WOOD),
    ...box(-3, 2, -3, 7, 1, 1, WOOD),
    ...box(-3, 2, 3, 7, 1, 1, WOOD),
    ...box(-3, 2, -3, 1, 1, 7, WOOD),
    ...box(3, 2, -3, 1, 1, 7, WOOD),
    // Fence posts (corners)
    { x: -3, y: 3, z: -3, color: WOOD },
    { x: 3, y: 3, z: -3, color: WOOD },
    { x: -3, y: 3, z: 3, color: WOOD },
    { x: 3, y: 3, z: 3, color: WOOD },
    // Sheep (3-voxel body + head)
    { x: -1, y: 1, z: 0, color: '#eeeeee' },
    { x: 0, y: 1, z: 0, color: '#eeeeee' },
    { x: 0, y: 2, z: 0, color: '#eeeeee' },
    { x: 1, y: 2, z: 0, color: '#444444' },
    { x: -2, y: 1, z: 2, color: '#eeeeee' },
    { x: -1, y: 1, z: 2, color: '#eeeeee' },
    { x: -2, y: 2, z: 2, color: '#eeeeee' },
    { x: -3, y: 2, z: 2, color: '#444444' },
  ],
};

const winery: StructurePrefab = {
  name: 'Winery',
  blocks: [
    // Stone press base
    ...box(-2, 0, -2, 5, 1, 5, STONE),
    ...box(-2, 1, -2, 5, 2, 5, '#aa6644'),
    // Thatched roof
    ...box(-2, 3, -2, 5, 1, 5, '#c9a063'),
    ...box(-1, 4, -1, 3, 1, 3, '#c9a063'),
    ...box(0, 5, 0, 1, 1, 1, '#c9a063'),
    // Wine press (large cask)
    { x: -1, y: 1, z: 1, color: '#5a3a1a' },
    { x: 0, y: 1, z: 1, color: '#5a3a1a' },
    { x: 1, y: 1, z: 1, color: '#5a3a1a' },
    { x: 0, y: 1, z: 2, color: '#8b1a1a' },
    // Vine trellis (T-posts with purple grapes)
    { x: -3, y: 1, z: -3, color: WOOD },
    { x: -3, y: 2, z: -3, color: WOOD },
    { x: -3, y: 3, z: -3, color: WOOD },
    { x: -2, y: 3, z: -3, color: '#5a2a5a' },
    { x: -1, y: 3, z: -3, color: '#5a2a5a' },
    { x: 3, y: 1, z: -3, color: WOOD },
    { x: 3, y: 2, z: -3, color: WOOD },
    { x: 3, y: 3, z: -3, color: WOOD },
    { x: 2, y: 3, z: -3, color: '#5a2a5a' },
    { x: 1, y: 3, z: -3, color: '#5a2a5a' },
  ],
};

const brewery: StructurePrefab = {
  name: 'Brewery',
  blocks: [
    ...box(-2, 0, -2, 5, 1, 5, STONE),
    // Tall brewhouse
    ...box(-2, 1, -2, 5, 4, 5, '#704030'),
    ...box(-2, 5, -2, 5, 1, 5, '#5a3a1a'),
    // Chimney (steam vent)
    ...box(2, 1, -2, 1, 6, 1, '#444444'),
    // Copper brewing vats visible
    { x: -1, y: 1, z: 2, color: '#c87838' },
    { x: 0, y: 1, z: 2, color: '#c87838' },
    { x: -1, y: 2, z: 2, color: '#c87838' },
    { x: 0, y: 2, z: 2, color: '#c87838' },
    // Beer barrel stack
    { x: -3, y: 1, z: 1, color: '#8b4513' },
    { x: -3, y: 2, z: 1, color: '#8b4513' },
    { x: -3, y: 1, z: 0, color: '#8b4513' },
    { x: -3, y: 2, z: 0, color: '#8b4513' },
    // Door
    ...box(0, 1, 2, 1, 2, 1, DARK_WOOD),
  ],
};

// ── Military ────────────────────────────────────────────────────────────

const assassinGuild: StructurePrefab = {
  name: "Assassin's Guild",
  blocks: [
    ...box(-1, 0, -1, 3, 1, 3, DARK_STONE),
    // Slender dark tower
    ...box(-1, 1, -1, 3, 7, 3, '#2a2a2a'),
    // Narrow windows (arrow slits)
    { x: 1, y: 3, z: 1, color: '#1a1a1a' },
    { x: 1, y: 5, z: 1, color: '#1a1a1a' },
    // Serrated crown
    { x: -2, y: 8, z: 0, color: '#1a1a1a' },
    { x: -2, y: 9, z: 0, color: '#1a1a1a' },
    { x: 0, y: 8, z: -2, color: '#1a1a1a' },
    { x: 0, y: 9, z: -2, color: '#1a1a1a' },
    { x: 2, y: 8, z: 0, color: '#1a1a1a' },
    { x: 2, y: 9, z: 0, color: '#1a1a1a' },
    { x: 0, y: 8, z: 2, color: '#1a1a1a' },
    { x: 0, y: 9, z: 2, color: '#1a1a1a' },
    // Crimson banner
    { x: 0, y: 6, z: 1, color: '#8b1a1a' },
    // Hidden door
    { x: 0, y: 1, z: 2, color: '#1a1a1a' },
    { x: 0, y: 2, z: 2, color: '#1a1a1a' },
  ],
};

const siegeWorkshop: StructurePrefab = {
  name: 'Siege Workshop',
  blocks: [
    ...box(-2, 0, -2, 5, 1, 5, STONE),
    // Open-air timber frame
    ...box(-2, 1, -2, 5, 1, 1, WOOD),
    ...box(-2, 1, 2, 5, 1, 1, WOOD),
    ...box(-2, 1, -2, 1, 1, 5, WOOD),
    ...box(2, 1, -2, 1, 1, 5, WOOD),
    ...box(-2, 4, -2, 5, 1, 1, WOOD),
    ...box(-2, 4, 2, 5, 1, 1, WOOD),
    ...box(-2, 4, -2, 1, 1, 5, WOOD),
    ...box(2, 4, -2, 1, 1, 5, WOOD),
    // Vertical posts
    { x: -2, y: 2, z: -2, color: WOOD },
    { x: 2, y: 2, z: -2, color: WOOD },
    { x: -2, y: 2, z: 2, color: WOOD },
    { x: 2, y: 2, z: 2, color: WOOD },
    { x: -2, y: 3, z: -2, color: WOOD },
    { x: 2, y: 3, z: -2, color: WOOD },
    { x: -2, y: 3, z: 2, color: WOOD },
    { x: 2, y: 3, z: 2, color: WOOD },
    // Catapult being built
    { x: 0, y: 1, z: 0, color: WOOD },
    { x: 1, y: 1, z: 0, color: WOOD },
    { x: -1, y: 1, z: 0, color: WOOD },
    { x: 0, y: 1, z: 1, color: WOOD },
    { x: 1, y: 1, z: 1, color: WOOD },
    { x: -1, y: 1, z: 1, color: WOOD },
    // Throwing arm (angled)
    { x: 0, y: 2, z: 0, color: WOOD },
    { x: 1, y: 3, z: 0, color: WOOD },
    { x: 2, y: 4, z: 0, color: WOOD },
    // Counterweight
    { x: -2, y: 2, z: 1, color: '#444444' },
  ],
};

const slaveMarket: StructurePrefab = {
  name: 'Slave Market',
  blocks: [
    ...box(-2, 0, -2, 5, 1, 5, STONE),
    // Wooden platform
    ...box(-2, 1, -2, 5, 1, 5, WOOD),
    // Auction block
    ...box(-1, 2, -1, 3, 1, 3, '#5a3a1a'),
    // Wooden cage with bars
    { x: -1, y: 3, z: -1, color: '#3a2010' },
    { x: 1, y: 3, z: -1, color: '#3a2010' },
    { x: -1, y: 4, z: -1, color: '#3a2010' },
    { x: 0, y: 4, z: -1, color: '#3a2010' },
    { x: 1, y: 4, z: -1, color: '#3a2010' },
    { x: -1, y: 5, z: -1, color: '#3a2010' },
    { x: 1, y: 5, z: -1, color: '#3a2010' },
    { x: -1, y: 3, z: 1, color: '#3a2010' },
    { x: 1, y: 3, z: 1, color: '#3a2010' },
    { x: -1, y: 4, z: 1, color: '#3a2010' },
    { x: 1, y: 4, z: 1, color: '#3a2010' },
    { x: -1, y: 5, z: 1, color: '#3a2010' },
    { x: 1, y: 5, z: 1, color: '#3a2010' },
    // Auctioneer's post
    { x: 0, y: 3, z: 1, color: WOOD },
    { x: 0, y: 4, z: 1, color: WOOD },
    // Whip-marked sign
    { x: 0, y: 5, z: 0, color: '#8b1a1a' },
  ],
};

// ── Fortifications (tiered) ────────────────────────────────────────────

function wallPrefab(tier: number): StructurePrefab {
  const height = 2 + tier;
  const crenellations = tier >= 2;
  const blocks: BlockVoxel[] = [
    // Base
    ...box(-3, 0, -1, 7, 1, 3, DARK_STONE),
    // Wall body
    ...box(-3, 1, -1, 7, height, 3, STONE),
  ];
  if (crenellations) {
    for (let x = -3; x <= 3; x += 2) {
      blocks.push({ x, y: height + 1, z: -1, color: STONE });
      blocks.push({ x, y: height + 1, z: 0, color: STONE });
      blocks.push({ x, y: height + 1, z: 1, color: STONE });
    }
  }
  if (tier >= 3) {
    // Reinforcement iron bands
    blocks.push({ x: -3, y: 2, z: 0, color: '#444444' });
    blocks.push({ x: 0, y: 2, z: 0, color: '#444444' });
    blocks.push({ x: 3, y: 2, z: 0, color: '#444444' });
    blocks.push({ x: -3, y: height, z: 0, color: '#444444' });
    blocks.push({ x: 0, y: height, z: 0, color: '#444444' });
    blocks.push({ x: 3, y: height, z: 0, color: '#444444' });
  }
  return {
    name: tier === 1 ? 'Wooden Wall' : tier === 2 ? 'Stone Wall' : 'Ironbound Wall',
    blocks,
  };
}

function watchTowerPrefab(tier: number): StructurePrefab {
  const height = 4 + tier * 2;
  const blocks: BlockVoxel[] = [
    // Foundation
    ...box(-1, 0, -1, 3, 1, 3, DARK_STONE),
    // Tower shaft
    ...box(-1, 1, -1, 3, height, 3, STONE),
    // Window slits
    { x: 0, y: Math.floor(height / 2), z: 1, color: '#1a1a1a' },
    { x: 0, y: Math.floor(height / 2) + 2, z: 1, color: '#1a1a1a' },
    // Platform crenellations
    { x: -2, y: height + 1, z: -2, color: STONE },
    { x: 0, y: height + 1, z: -2, color: STONE },
    { x: 2, y: height + 1, z: -2, color: STONE },
    { x: -2, y: height + 1, z: 2, color: STONE },
    { x: 0, y: height + 1, z: 2, color: STONE },
    { x: 2, y: height + 1, z: 2, color: STONE },
  ];
  if (tier >= 2) {
    // Pointed roof
    blocks.push({ x: 0, y: height + 2, z: 0, color: '#8b1a1a' });
    blocks.push({ x: -1, y: height + 2, z: 0, color: '#8b1a1a' });
    blocks.push({ x: 1, y: height + 2, z: 0, color: '#8b1a1a' });
    blocks.push({ x: 0, y: height + 3, z: 0, color: '#5a0a0a' });
  }
  if (tier >= 3) {
    // Warning beacon
    blocks.push({ x: 0, y: height + 2, z: 0, color: '#ff6622' });
  }
  return {
    name: tier === 1 ? 'Watchtower' : tier === 2 ? 'Guard Tower' : 'Wizard Tower',
    blocks,
  };
}

// ── Social (theater, colosseum) ────────────────────────────────────────

const theater: StructurePrefab = {
  name: 'Theater',
  blocks: [
    ...box(-2, 0, -2, 5, 1, 5, STONE),
    // Stage
    ...box(-2, 1, -2, 5, 1, 3, WOOD),
    // Stage backdrop
    ...box(-2, 2, -2, 5, 3, 1, '#7a1a1a'),
    // Curtain
    ...box(-1, 2, -1, 3, 3, 1, '#8b1a1a'),
    // Proscenium columns
    { x: -2, y: 2, z: 1, color: '#e0e0e0' },
    { x: -2, y: 3, z: 1, color: '#e0e0e0' },
    { x: -2, y: 4, z: 1, color: '#e0e0e0' },
    { x: 2, y: 2, z: 1, color: '#e0e0e0' },
    { x: 2, y: 3, z: 1, color: '#e0e0e0' },
    { x: 2, y: 4, z: 1, color: '#e0e0e0' },
    // Roof / marquee
    ...box(-2, 5, -2, 5, 1, 3, '#d4af37'),
    // Audience seating (semi-circular rows)
    { x: -1, y: 1, z: 2, color: '#5a3a1a' },
    { x: 0, y: 1, z: 2, color: '#5a3a1a' },
    { x: 1, y: 1, z: 2, color: '#5a3a1a' },
    { x: -2, y: 1, z: 1, color: '#5a3a1a' },
    { x: 2, y: 1, z: 1, color: '#5a3a1a' },
    // Marquee sign with gold trim
    { x: 0, y: 5, z: 1, color: '#ffcc44' },
  ],
};

const colosseum: StructurePrefab = {
  name: 'Colosseum',
  blocks: [
    // Large oval foundation (approximated)
    ...box(-4, 0, -4, 9, 1, 9, DARK_STONE),
    // Outer ring wall (lower tier)
    ...box(-4, 1, -4, 9, 2, 1, STONE),
    ...box(-4, 1, 4, 9, 2, 1, STONE),
    ...box(-4, 1, -4, 1, 2, 9, STONE),
    ...box(4, 1, -4, 1, 2, 9, STONE),
    // Middle tier with arches
    ...box(-4, 3, -4, 9, 2, 1, STONE),
    ...box(-4, 3, 4, 9, 2, 1, STONE),
    ...box(-4, 3, -4, 1, 2, 9, STONE),
    ...box(4, 3, -4, 1, 2, 9, STONE),
    // Arch windows (dark)
    { x: -3, y: 4, z: -4, color: '#1a1a1a' },
    { x: 0, y: 4, z: -4, color: '#1a1a1a' },
    { x: 3, y: 4, z: -4, color: '#1a1a1a' },
    { x: -3, y: 4, z: 4, color: '#1a1a1a' },
    { x: 0, y: 4, z: 4, color: '#1a1a1a' },
    { x: 3, y: 4, z: 4, color: '#1a1a1a' },
    // Upper tier
    ...box(-4, 5, -4, 9, 1, 1, STONE),
    ...box(-4, 5, 4, 9, 1, 1, STONE),
    ...box(-4, 5, -4, 1, 1, 9, STONE),
    ...box(4, 5, -4, 1, 1, 9, STONE),
    // Arena floor (sand)
    ...box(-3, 1, -3, 7, 1, 7, '#d4a04a'),
    // Gladiator gate
    { x: 0, y: 1, z: 4, color: DARK_WOOD },
    { x: 0, y: 2, z: 4, color: DARK_WOOD },
    // Imperial box
    ...box(-1, 6, -4, 3, 1, 1, '#d4af37'),
  ],
};

// ─── Military / Resource (barracks, mine) ──────────────────

// Military training hall: stone block base, timber upper floor, red tile
// roof, flag pole on top, central doorway, narrow slit windows. ~8×8
// footprint, ~10 tall.
const barracks: StructurePrefab = {
  name: 'Barracks',
  blocks: [
    // Stone foundation (8x8 footprint, centered)
    ...box(-3, 0, -3, 8, 1, 8, STONE),
    // Stone ground floor walls
    ...box(-3, 1, -3, 8, 3, 8, '#7d7a72'),
    // Timber upper floor walls
    ...box(-3, 4, -3, 8, 3, 8, '#6a4a2a'),
    // Doorway (front, center) — dark wood door
    ...box(0, 1, 4, 2, 3, 1, DARK_WOOD),
    // Narrow slit windows on the upper floor (front)
    { x: -2, y: 5, z: 4, color: '#1a1a1a' },
    { x: 3, y: 5, z: 4, color: '#1a1a1a' },
    // Narrow slit windows on the sides
    { x: -3, y: 5, z: 0, color: '#1a1a1a' },
    { x: 4, y: 5, z: 0, color: '#1a1a1a' },
    // Red tile roof — pitched (wide lower row, narrow upper row)
    ...box(-3, 7, -3, 8, 1, 8, '#8a2a2a'),
    ...box(-2, 8, -2, 6, 1, 6, '#7a1a1a'),
    ...box(-1, 9, -1, 4, 1, 4, '#6a0a0a'),
    // Ridge cap
    ...box(-1, 10, -1, 4, 1, 1, '#5a0000'),
    // Flag pole on the roof ridge
    { x: 1, y: 11, z: 0, color: '#3a3a3a' },
    { x: 1, y: 12, z: 0, color: '#3a3a3a' },
    { x: 1, y: 13, z: 0, color: '#3a3a3a' },
    // Crimson banner flag
    { x: 2, y: 12, z: 0, color: '#8b1a1a' },
    { x: 2, y: 13, z: 0, color: '#8b1a1a' },
    // Stone steps in front of the door
    { x: 0, y: 0, z: 5, color: STONE },
    { x: 1, y: 0, z: 5, color: STONE },
  ],
};

// Mine entrance: wooden support beams framing a dark opening, pile of rubble
// out front, mine cart tracks leading in, small lantern post. ~7 wide, ~6 tall.
const mine: StructurePrefab = {
  name: 'Mine',
  blocks: [
    // Hillside / rubble base (stone)
    ...box(-3, 0, -2, 7, 1, 4, '#7d7a72'),
    // Dark opening (the adit) — 3 wide, 3 tall, set into the hillside
    ...box(-1, 1, -1, 3, 3, 1, '#1a1a1a'),
    // Wooden support frame around the opening (lintel + side posts)
    // Left post
    ...box(-2, 1, -1, 1, 4, 1, '#5a3a2a'),
    // Right post
    ...box(2, 1, -1, 1, 4, 1, '#5a3a2a'),
    // Top lintel beam
    ...box(-2, 5, -1, 5, 1, 1, '#5a3a2a'),
    // Cross-brace above the lintel (headframe)
    ...box(-2, 6, -1, 5, 1, 1, '#3a2010'),
    // Headframe tower (a small derrick over the entrance)
    { x: -3, y: 6, z: -1, color: '#5a3a2a' },
    { x: 3, y: 6, z: -1, color: '#5a3a2a' },
    { x: -3, y: 7, z: -1, color: '#3a2010' },
    { x: 3, y: 7, z: -1, color: '#3a2010' },
    { x: -3, y: 8, z: -1, color: '#3a2010' },
    { x: 3, y: 8, z: -1, color: '#3a2010' },
    // Pulley wheel at the top of the headframe
    { x: 0, y: 8, z: -1, color: '#444444' },
    // Pile of rubble out front
    { x: -3, y: 1, z: 1, color: '#9a9a92' },
    { x: -3, y: 1, z: 2, color: '#7d7a72' },
    { x: -2, y: 1, z: 2, color: '#9a9a92' },
    { x: 3, y: 1, z: 1, color: '#9a9a92' },
    { x: 3, y: 1, z: 2, color: '#7d7a72' },
    { x: 2, y: 1, z: 2, color: '#9a9a92' },
    // Mine cart tracks (dark line) leading into the opening
    { x: -1, y: 1, z: 1, color: '#2a2a2a' },
    { x: 0, y: 1, z: 1, color: '#2a2a2a' },
    { x: 1, y: 1, z: 1, color: '#2a2a2a' },
    { x: -1, y: 1, z: 2, color: '#2a2a2a' },
    { x: 0, y: 1, z: 2, color: '#2a2a2a' },
    { x: 1, y: 1, z: 2, color: '#2a2a2a' },
    // Mine cart on the tracks (just outside the entrance)
    { x: -1, y: 2, z: 2, color: '#5a3a2a' },
    { x: 0, y: 2, z: 2, color: '#5a3a2a' },
    { x: 1, y: 2, z: 2, color: '#5a3a2a' },
    { x: 0, y: 3, z: 2, color: '#3a2010' },
    // Lantern post to the left of the entrance
    { x: -3, y: 1, z: 0, color: '#3a2010' },
    { x: -3, y: 2, z: 0, color: '#3a2010' },
    { x: -3, y: 3, z: 0, color: '#3a2010' },
    // Glowing lantern head
    { x: -3, y: 4, z: 0, color: '#ffcc44', emissive: '#ff8800' },
  ],
};

export const BUILDING_PREFABS_EXTRA: Readonly<Record<string, StructurePrefab>> = {
  forge_hut: forgeHut,
  granary,
  well,
  herbalist_hut: herbalistHut,
  library,
  bank,
  pasture,
  winery,
  brewery,
  assassin_guild: assassinGuild,
  siege_workshop: siegeWorkshop,
  slave_market: slaveMarket,
  walls_1: wallPrefab(1),
  walls_2: wallPrefab(2),
  walls_3: wallPrefab(3),
  tower_1: watchTowerPrefab(1),
  tower_2: watchTowerPrefab(2),
  tower_3: watchTowerPrefab(3),
  theater,
  colosseum,
  barracks,
  mine,
};
